"use client";

import React from "react";
import { CircleCheck as CheckCircle2, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const easeCurve = [0.16, 1, 0.3, 1] as const;
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: shouldReduceMotion ? {
      opacity: 1,
      y: 0
    } : {
      opacity: 0,
      y: 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.35,
        ease: easeCurve
      }
    }
  };
  const benefits = ["Independent access to multiple insurance carriers", "Retirement income and protection options based on individual needs", "Clear information so you can understand your choices."];
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-16 md:py-24 lg:py-28">
      <div className="mx-auto max-w-[1240px] px-5 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-8 xl:gap-12">
          {/* Text Content Column */}
          <motion.div variants={containerVariants} initial="hidden" animate="visible" className="lg:col-span-7 flex flex-col items-start">
            {/* Context Eyebrow Badge */}
            <motion.div variants={itemVariants} className="mb-4">
              <Badge variant="outline" className="gap-1.5 rounded-full border-border bg-card px-3.5 py-1 text-xs font-semibold text-foreground tracking-wide">
                <ShieldCheck className="size-3.5 text-foreground" />
                <span>INDEPENDENT INSURANCE & RETIREMENT INCOME CONSULTING</span>
              </Badge>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 variants={itemVariants} className="font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-[3.25rem] lg:leading-[1.15]">
              Clear guidance for life insurance and retirement income.
            </motion.h1>

            {/* Supporting Description */}
            <motion.p variants={itemVariants} className="mt-6 text-base text-muted-foreground sm:text-lg sm:leading-relaxed max-w-2xl">
              Get clear, straightforward information about life insurance, fixed and fixed indexed annuities, and income protection. Understand how insurance products work, what questions to ask, and which options may fit your needs.
            </motion.p>

            {/* Key Assurance Points */}
            <motion.ul variants={itemVariants} className="mt-6 flex flex-col gap-2.5 sm:gap-3">
              {benefits.map((benefit, i) => <li key={benefit} data-index={i} className="flex items-center gap-2.5 text-sm font-medium text-foreground sm:text-base">
                  <CheckCircle2 className="size-4 shrink-0 text-foreground" />
                  <span>{benefit}</span>
                </li>)}
            </motion.ul>
          </motion.div>

          {/* Media Column / Guidance Plinth Frame */}
          <motion.div variants={itemVariants} initial="hidden" animate="visible" className="lg:col-span-5">
            <div className="relative mx-auto w-full max-w-lg lg:max-w-none">
              {/* Outer Plinth Frame */}
              <div className="relative rounded-2xl border border-border bg-card p-3 shadow-md sm:p-4">
                <div className="relative aspect-[4/3] sm:aspect-[5/4] w-full overflow-hidden rounded-xl bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796870/img/djtd_consulting_advisory_meeting.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796870/img/djtd_consulting_advisory_meeting.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796870/img/djtd_consulting_advisory_meeting.png-wvc-sizes" alt="Licensed insurance professional discussing retirement planning options with a couple in a sunlit office" loading="lazy" decoding="async" className="h-full w-full object-cover object-center" />
                </div>

                {/* Educational Callout Capsule */}
                <div className="mt-3 flex items-center justify-between rounded-lg border border-border bg-[hsl(350_35%_96%)] px-4 py-3 sm:mt-4">
                  <div>
                    <p className="text-xs font-semibold text-foreground">
                      Your Needs Guide the Search.
                    </p>
                    <p className="text-xs text-muted-foreground">
                      We listen first, then compare options from multiple carriers based on your needs and questions.
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full border border-border bg-card px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                    Independent
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}